// The shared mechanism behind the live views that reload rather than patch.
//
// Appointments and invoices can each be refreshed one row at a time, because the API can return one
// of them by id. Tasks, the surgery waitlist and calendar availability cannot: their list endpoints
// are paginated queries with no single-record GET, and availability records expand into many events
// on screen. For those, the only correct refresh is the view's own fetch.
//
// Which makes two things matter, and this service exists to get both right in one place:
//
//   - Deciding not to ask. A reload is only worth making when the change is something the view is
//     actually showing, so every subscriber supplies an isRelevant test. That test is the saving.
//   - Asking once. A poll delivers a whole batch of events, and one user action can publish several
//     - saving a weekly availability pattern publishes an event per weekday, and a task publishes to
//     both the practice channel and the assignee's user channel. Without coalescing, each of those
//     would fire its own reload of the same list.
app.service("LiveReloadService", [
	"$timeout",
	"LiveUpdateGuard",
	function ($timeout, LiveUpdateGuard) {
		/** Long enough to gather a batch, short enough that the change still looks immediate. */
		const DEFAULT_COALESCE_MS = 300;

		/**
		 * Wires a view up to a set of push events for the life of its scope.
		 *
		 * @param scope the view's scope; listeners are removed when it is destroyed
		 * @param spec  events - the PUSH_EVENTS broadcast names to listen for
		 *              isRelevant(payload, pushEvent) - whether the change is something this view is
		 *                  showing. Should be as tight as the view's own query; a false here is a
		 *                  request not made.
		 *              reload() - the view's own fetch
		 *              handleLocally(payload, pushEvent) - optional. Apply the change without a
		 *                  request, returning whether it did; false falls through to reload().
		 *              coalesceMs - optional override of the batching window
		 *
		 * The reload runs through LiveUpdateGuard, keyed by the events it listens for, so a view whose
		 * fetch keeps failing stops being reloaded rather than failing again on every event.
		 */
		function subscribe(scope, spec) {
			const coalesceMs = spec.coalesceMs === undefined ? DEFAULT_COALESCE_MS : spec.coalesceMs;
			const guardKey = "reload:" + spec.events.join(",");
			let pendingReload = null;

			/** Queues one reload, which every event still waiting in the window shares. */
			function reloadSoon() {
				if (pendingReload) {
					return;
				}
				pendingReload = $timeout(function () {
					pendingReload = null;
					// Checked first rather than left to run(): nothing here would handle its rejection.
					if (LiveUpdateGuard.isStopped(guardKey)) {
						return;
					}
					LiveUpdateGuard.run(guardKey, function () {
						return spec.reload();
					});
				}, coalesceMs);
			}

			function onEvent(pushEvent) {
				const payload = (pushEvent && pushEvent.data) || {};
				if (!spec.isRelevant(payload, pushEvent)) {
					return;
				}
				if (spec.handleLocally && spec.handleLocally(payload, pushEvent)) {
					return;
				}
				reloadSoon();
			}

			spec.events.forEach(function (eventName) {
				scope.$on(eventName, function (e, pushEvent) {
					onEvent(pushEvent);
				});
			});

			// A queued reload must not outlive the view. Several of these views are modals, and firing
			// into a closed one re-renders something that is no longer there.
			scope.$on("$destroy", function () {
				if (pendingReload) {
					$timeout.cancel(pendingReload);
					pendingReload = null;
				}
			});
		}

		const service = {
			subscribe: subscribe,

			/**
			 * Whether two ids name the same thing. Ids reach a view from an event payload and from a
			 * list response by different routes and do not always agree on type, and a strict comparison
			 * that disagreed would silently report the record as absent.
			 */
			sameId: function (a, b) {
				return a !== null && a !== undefined && b !== null && b !== undefined && String(a) === String(b);
			},
		};

		return service;
	},
]);
